import { captureException } from '@/utils/sentry';
import { Coordinate } from '@/experiences/sales-portal/components/map/types';
import { CrmCheckInInteraction } from '@/types';
import { useSalesPortalCreateCrmActivityMutation } from '@/experiences/sales-portal/blocs/documents/sales-portal-create-crm-activity.generated';

export interface CreateCrmActivityParams {
  chatUUID: string;
  interactions: CrmCheckInInteraction[];
  location?: Coordinate;
  note?: string;
  remindAt?: Date;
}

export interface CreateCrmActivityMutatorOptions {
  onError?: (error: Error) => void;
}

export interface CreateCrmActivityMutatorResult {
  createCrmActivity: (
    params: CreateCrmActivityParams,
  ) => Promise<string | null>;
  loading: boolean;
}

/**
 * Creates a check-in activity with an optional note and reminder in one call.
 */
export const useCreateCrmActivityMutator = (
  options?: CreateCrmActivityMutatorOptions,
): CreateCrmActivityMutatorResult => {
  const [createCrmActivityMutation, { loading }] =
    useSalesPortalCreateCrmActivityMutation();

  const createCrmActivity = async (params: CreateCrmActivityParams) => {
    const { chatUUID, interactions, location, note, remindAt } = params;

    try {
      const result = await createCrmActivityMutation({
        variables: {
          chatUUID,
          checkIn: {
            interactions,
            latitude: location?.latitude,
            longitude: location?.longitude,
          },
          note: note ? { content: note } : undefined,
          reminder: remindAt
            ? { remind_at: remindAt.toISOString() }
            : undefined,
        },
      });

      const activityUUID = result.data?.createCrmActivity.activity_id;
      if (!activityUUID) {
        const error = new Error('createCrmActivity returned no activity_id');
        captureException(error, {
          extra: { response: result.data?.createCrmActivity },
        });
        options?.onError?.(error);
        return null;
      }

      return activityUUID;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      options?.onError?.(error);
      return null;
    }
  };

  return {
    createCrmActivity,
    loading,
  };
};
